import type { Editor, EditorEvents } from '@tiptap/react';
import { v4 } from 'uuid';

export type Event =
  | 'editor-updated'
  | 'listener-created'
  | 'element-clicked'
  | 'saved'
  | 'saving';
export type EventFnArgs = {
  id: string;
  editor?: Editor;
  command?: string;
  element?: HTMLElement;
  event?: PublishedEditorUpdateEvents;
  document: object;
  html: string;
};
type SaveEvents = Pick<EventFnArgs, 'id' | 'editor' | 'document' | 'html'>;
export type UpdateEvents = Pick<
  EditorEvents,
  'update' | 'selectionUpdate' | 'focus'
>;
export type PublishedEditorUpdateEvents =
  | keyof UpdateEvents
  | 'listener-created'
  | 'broadcaster-created'
  | null;
export type EventFn = ({ id, editor, command }: EventFnArgs) => void;

class Subscriber {
  id: string;

  constructor() {
    this.id = v4();
  }
}

export class TeaPubSub {
  static #instance: TeaPubSub;
  events: Record<Event, { id: string; fn: EventFn; subscriber: Subscriber }[]>;

  private constructor() {
    this.events = {
      'editor-updated': [],
      'listener-created': [],
      'element-clicked': [],
      saved: [],
      saving: [],
    };
  }

  public static get instance(): TeaPubSub {
    if (!TeaPubSub.#instance) {
      TeaPubSub.#instance = new TeaPubSub();
    }

    return TeaPubSub.#instance;
  }

  subscribe(event: Event, id: string, fn: EventFn): Subscriber {
    if (!this.events[event]) {
      this.events[event] = [];
    }

    const subscriber = new Subscriber();
    this.events[event].push({ id, subscriber, fn });

    return subscriber;
  }

  unsubscribe(event: Event, subscriberId: string): void {
    this.events[event] = this.events[event]?.filter(
      (e) => e.subscriber.id !== subscriberId,
    );
  }

  publish(
    event: 'element-clicked',
    data: Pick<EventFnArgs, 'id' | 'element'>,
  ): void;
  publish(event: 'listener-created', data: Pick<EventFnArgs, 'id'>): void;
  publish(
    event: 'editor-updated',
    data: Pick<EventFnArgs, 'id' | 'editor'> & {
      event?: PublishedEditorUpdateEvents;
    },
  ): void;
  publish(event: 'saved', data: SaveEvents): void;
  publish(event: 'saving', data: SaveEvents): void;

  publish(event: Event, data: EventFnArgs): void {
    const events = this.events[event];

    if (events) {
      for (const e of events) {
        if (e.id === data.id) {
          e.fn(data);
        }
      }
    }
  }

  clear() {
    this.events = {
      'editor-updated': [],
      'listener-created': [],
      'element-clicked': [],
      saved: [],
      saving: [],
    };
  }
}
